import type { HTMLAttributes, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

import {
    FloatingFocusManager,
    FloatingPortal,
    type Placement,
    type Strategy,
    arrow,
    autoUpdate,
    offset,
    shift,
    size,
    useClick,
    useDismiss,
    useFloating,
    useHover,
    useId,
    useInteractions,
    useTransitionStyles,
} from '@floating-ui/react';
import { clsx } from 'clsx';

interface PopoverProps {
    position?: Placement;
    children: ReactNode;
    content: ({ close }: { close?: () => void }) => ReactNode;
    dataTestid?: string;
    htmlAttributes?: HTMLAttributes<HTMLDivElement>;
    defaultOpen?: boolean;
    open?: boolean;
    openState?: boolean;
    setOpenState?: (open: boolean) => void;
    openOnHover?: boolean;
    disabled?: boolean;
    strategy?: Strategy;
    inPortal?: boolean; // Use FloatingPortal or not
    zIndex?: number;
    referenceClassName?: string;
}

/*
 * Implementation inspired by https://floating-ui.com/docs/popover
 */
export const Popover = ({
    position = 'top',
    content,
    children,
    dataTestid,
    htmlAttributes,
    defaultOpen = false,
    openState = defaultOpen,
    setOpenState, // for stateless behavior
    openOnHover = false,
    disabled = false,
    strategy = 'fixed',
    inPortal = true,
    zIndex = 3147483001,
    referenceClassName,
}: PopoverProps) => {
    const [isOpen, setIsOpen] = useState(!disabled ? defaultOpen : false);
    useEffect(() => {
        if (typeof openState === 'boolean') {
            setIsOpen(openState);
        }
    }, [openState]);
    const arrowRef = useRef(null);
    const [maxHeight, setMaxHeight] = useState<string>('none');

    const { refs, floatingStyles, context } = useFloating({
        open: setOpenState ? openState : isOpen,
        onOpenChange: setOpenState || setIsOpen,
        placement: position,
        strategy,
        middleware: [
            offset(10),
            shift(),
            arrow({ element: arrowRef }),
            size({
                apply({ availableHeight }) {
                    flushSync(() => setMaxHeight(`${availableHeight - 20}px`));
                },
            }),
        ],
        whileElementsMounted: autoUpdate,
    });

    const click = useClick(context, { enabled: !openOnHover });
    const hover = useHover(context, { enabled: openOnHover });
    const dismiss = useDismiss(context);
    const { styles, isMounted } = useTransitionStyles(context);
    const { getReferenceProps, getFloatingProps } = useInteractions([openOnHover ? hover : click, dismiss]);
    const headingId = useId();

    const floatingContent = (
        <FloatingFocusManager
            context={context}
            modal={false}
            initialFocus={-1}
        >
            <div
                className='max-w-xl overflow-y-auto rounded-2xl bg-white p-4 text-gray-900 drop-shadow-lg'
                ref={refs.setFloating}
                style={{ ...floatingStyles, maxHeight, position: 'absolute', zIndex }}
                aria-labelledby={headingId}
                data-testid={dataTestid}
                data-component-name='Popover'
                {...htmlAttributes}
                {...getFloatingProps()}
            >
                {isMounted && (
                    <div style={styles}>{content({ close: () => (setOpenState ? false : setIsOpen(false)) })}</div>
                )}
            </div>
        </FloatingFocusManager>
    );

    return (
        <>
            <div
                ref={refs.setReference}
                className={clsx(referenceClassName)}
                {...(!disabled ? getReferenceProps() : {})}
            >
                {children}
            </div>

            {isOpen &&
                (inPortal ? (
                    <FloatingPortal root={refs.floating.current?.parentElement || document.body}>
                        {floatingContent}
                    </FloatingPortal>
                ) : (
                    floatingContent
                ))}
        </>
    );
};
